import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';
import { calculateProfileCompleteness } from '../utils/profileUtils';
import api from '../api';
import { extractHoldingsFromImage } from '../utils/ocrHoldings';
import './UserProfile.css';

const STEPS = [
  { number: 1, label: 'Currency' },
  { number: 2, label: 'Holdings' },
  { number: 3, label: 'Preferences' }
];

const INVESTMENT_GOALS = [
  'Retirement', 'Wealth Building', 'Income Generation', 'Education Fund',
  'Emergency Fund', 'Home Purchase', 'Short-term Gains'
];

const SECTORS = [
  'Technology', 'Healthcare', 'Finance', 'Energy', 'Real Estate',
  'Consumer Goods', 'Industrial', 'Utilities', 'Materials'
];

const UserProfile = ({ userProfile, onUpdateProfile, onClose, inline = false }) => {
  const { currency, setCurrency, currencyData, availableCurrencies } = useCurrency();

  const [tickerValidation, setTickerValidation] = useState({});
  const validationTimers = useRef({});

  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }
  const [importStatus, setImportStatus] = useState(null);

  const [formData, setFormData] = useState({
    investment_goals: [],
    risk_tolerance: '',
    investment_horizon: '',
    monthly_investment: '',
    current_portfolio: '',
    preferred_sectors: [],
    investment_style: '',
    holdings: [],
    currency: currency
  });

  useEffect(() => {
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        ...userProfile,
        holdings: userProfile.holdings || [],
        currency: userProfile.currency || currency
      }));
    }
  }, [userProfile, currency]);

  const completeness = calculateProfileCompleteness(formData);

  // Read tickers and share counts out of a brokerage screenshot, in the browser.
  const handleImportImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setImportStatus('Reading the screenshot on this device…');
    try {
      const { holdings } = await extractHoldingsFromImage(file, (p) => setImportStatus(`Reading… ${Math.round(p * 100)}%`));
      if (!holdings.length) {
        setImportStatus('No tickers with share counts were found in that image.');
        return;
      }
      setFormData((prev) => {
        const rows = [...(prev.holdings || [])].filter((h) => h.ticker || h.quantity || h.avgPrice);
        holdings.forEach((h) => {
          const existing = rows.find((r) => String(r.ticker || '').toUpperCase() === h.ticker);
          if (existing) existing.quantity = String(Number(existing.quantity || 0) + h.quantity);
          else rows.push({ ticker: h.ticker, quantity: String(h.quantity), avgPrice: h.avgPrice === '' ? '' : String(h.avgPrice) });
        });
        return { ...prev, holdings: rows };
      });
      setImportStatus(`Added ${holdings.length} holdings from the screenshot. Check the numbers before saving.`);
    } catch (err) {
      setImportStatus(err.message || 'Could not read that image.');
    }
  };

  // ── Handlers ──

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCurrencyChange = (newCurrency) => {
    const prevCurrency = formData.currency;
    setCurrency(newCurrency);
    setFormData(prev => ({ ...prev, currency: newCurrency }));

    // Re-validate existing tickers if currency changed
    if (prevCurrency !== newCurrency) {
      (formData.holdings || []).forEach((holding, index) => {
        if (holding.ticker && holding.ticker.trim()) {
          // Strip old suffix if switching currencies
          let bare = holding.ticker.replace(/\.(NS|BO)$/i, '');
          const updatedHoldings = [...(formData.holdings || [])];
          updatedHoldings[index] = { ...updatedHoldings[index], ticker: bare };
          setFormData(prev => ({ ...prev, holdings: updatedHoldings }));
          validateTicker(index, bare, newCurrency);
        }
      });
    }
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  // ── Holdings ──

  const addHolding = () => {
    setFormData(prev => ({
      ...prev,
      holdings: [...(prev.holdings || []), { ticker: '', quantity: '', avgPrice: '' }]
    }));
  };

  const removeHolding = (index) => {
    setFormData(prev => ({
      ...prev,
      holdings: (prev.holdings || []).filter((_, i) => i !== index)
    }));
    setTickerValidation(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const validateTicker = useCallback((index, symbol, currencyOverride) => {
    if (validationTimers.current[index]) {
      clearTimeout(validationTimers.current[index]);
    }

    const trimmed = symbol.trim().toUpperCase();

    if (!trimmed) {
      setTickerValidation(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }

    setTickerValidation(prev => ({ ...prev, [index]: { loading: true } }));

    validationTimers.current[index] = setTimeout(async () => {
      try {
        const cur = currencyOverride || formData.currency || currency;
        const res = await api.get(
          `/v1/market/validate/${encodeURIComponent(trimmed)}?currency=${cur}`
        );

        // If backend resolved to a different symbol (e.g. RELIANCE → RELIANCE.NS)
        if (res.data.valid && res.data.symbol !== trimmed) {
          setFormData(prev => {
            const updated = [...(prev.holdings || [])];
            if (updated[index]) {
              updated[index] = { ...updated[index], ticker: res.data.symbol };
            }
            return { ...prev, holdings: updated };
          });
        }

        setTickerValidation(prev => ({
          ...prev,
          [index]: {
            valid: res.data.valid,
            name: res.data.name || '',
            loading: false,
            error: res.data.valid ? null : (res.data.message || 'Invalid symbol')
          }
        }));
      } catch (err) {
        setTickerValidation(prev => ({
          ...prev,
          [index]: { valid: false, loading: false, error: 'Could not validate' }
        }));
      }
    }, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.currency, currency]);

  const updateHolding = (index, field, value) => {
    const updatedHoldings = [...(formData.holdings || [])];
    updatedHoldings[index] = {
      ...updatedHoldings[index],
      [field]: field === 'ticker' ? value.toUpperCase() : value
    };
    setFormData(prev => ({ ...prev, holdings: updatedHoldings }));

    if (field === 'ticker') {
      validateTicker(index, value);
    }
  };

  // ── Submit ──

  const handleSave = async () => {
    const invalidTickers = (formData.holdings || []).filter((h, i) =>
      h.ticker && tickerValidation[i]?.valid === false
    );

    if (invalidTickers.length > 0) {
      const symbols = invalidTickers.map(h => h.ticker).join(', ');
      if (!window.confirm(`The following tickers could not be verified: ${symbols}\n\nSave anyway?`)) {
        return;
      }
    }

    setSaving(true);
    try {
      await onUpdateProfile(formData);
      setToast({ type: 'success', message: 'Profile saved!' });
      setTimeout(() => {
        setToast(null);
        if (onClose) onClose();
      }, 1200);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to save. Please try again.' });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ── Step completeness indicators ──

  const isStepComplete = (step) => {
    if (step === 1) return !!formData.currency;
    if (step === 2) return (formData.holdings || []).length > 0 && formData.holdings.some(h => h.ticker);
    if (step === 3) return !!formData.risk_tolerance || formData.investment_goals.length > 0;
    return false;
  };

  // ── Render ──

  const currentCur = formData.currency || currency;

  return (
    <div className={inline ? "profile-inline" : "profile-modal"} onClick={inline ? undefined : (e) => e.target === e.currentTarget && onClose()}>
      <div className="profile-content">
        {/* Header */}
        <div className="profile-header">
          <h2>Financial Profile</h2>
          {!inline && <button className="close-btn" onClick={onClose}>×</button>}
        </div>

        {/* Progress bar */}
        <div className="profile-progress">
          <div className="progress-text">Profile {completeness}% complete</div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${completeness}%` }} />
          </div>
        </div>

        {/* Wizard step indicators */}
        <div className="wizard-steps">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.number}>
              {i > 0 && <div className={`step-connector ${isStepComplete(step.number - 1) ? 'completed' : ''}`} />}
              <button
                type="button"
                className={`wizard-step ${currentStep === step.number ? 'active' : ''} ${isStepComplete(step.number) ? 'completed' : ''}`}
                onClick={() => setCurrentStep(step.number)}
              >
                <span className="step-number">
                  {isStepComplete(step.number) && currentStep !== step.number ? '\u2713' : step.number}
                </span>
                <span className="step-label">{step.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Toast */}
        {toast && (
          <div className={`profile-toast ${toast.type}`} role="alert">
            <span className="toast-icon">{toast.type === 'success' ? '\u2713' : '!'}</span>
            <span>{toast.message}</span>
          </div>
        )}

        {/* Step content */}
        <div className="profile-form">

          {/* ─── STEP 1: Currency ─── */}
          {currentStep === 1 && (
            <div className="step-content">
              <div className="form-section">
                <h3>Select Your Currency</h3>
                <p className="step-description">
                  This sets your market and determines how tickers and prices work throughout the app.
                </p>
                <div className="currency-cards">
                  {Object.entries(availableCurrencies).map(([code, info]) => (
                    <button
                      key={code}
                      type="button"
                      className={`currency-card ${currentCur === code ? 'selected' : ''}`}
                      onClick={() => handleCurrencyChange(code)}
                    >
                      <span className="currency-symbol">{info.symbol}</span>
                      <span className="currency-name">{info.name}</span>
                      <span className="currency-code">{code}</span>
                      {code === 'INR' && <span className="currency-hint">NSE / BSE stocks</span>}
                      {code === 'USD' && <span className="currency-hint">US market stocks</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── STEP 2: Holdings ─── */}
          {currentStep === 2 && (
            <div className="step-content">
              <div className="form-section">
                <h3>Your Holdings</h3>
                <p className="step-description">
                  {currentCur === 'INR'
                    ? 'Enter your stock symbols (e.g. RELIANCE, TCS, INFY). We\'ll auto-detect the NSE listing.'
                    : 'Enter your stock symbols (e.g. AAPL, MSFT, GOOGL).'}
                </p>
                {(formData.holdings || []).map((holding, index) => (
                  <div key={index} className="holding-row">
                    <div className="ticker-input-wrapper">
                      <input
                        type="text"
                        placeholder={currentCur === 'INR' ? 'e.g. RELIANCE' : 'e.g. AAPL'}
                        value={holding.ticker}
                        onChange={(e) => updateHolding(index, 'ticker', e.target.value)}
                        className={`holding-input ${
                          tickerValidation[index]?.valid === true ? 'ticker-valid' :
                          tickerValidation[index]?.valid === false ? 'ticker-invalid' : ''
                        }`}
                      />
                      {tickerValidation[index]?.loading && (
                        <span className="ticker-status loading">...</span>
                      )}
                      {tickerValidation[index]?.valid === true && (
                        <span className="ticker-status valid" title={tickerValidation[index].name}>&#10003;</span>
                      )}
                      {tickerValidation[index]?.valid === false && !tickerValidation[index]?.loading && (
                        <span className="ticker-status invalid" title={tickerValidation[index].error}>&#10007;</span>
                      )}
                    </div>
                    <input
                      type="number"
                      placeholder="Qty"
                      value={holding.quantity}
                      onChange={(e) => updateHolding(index, 'quantity', e.target.value)}
                      className="holding-input"
                      min="0"
                    />
                    <input
                      type="number"
                      placeholder="Avg cost (stock's own currency)"
                      value={holding.avgPrice}
                      onChange={(e) => updateHolding(index, 'avgPrice', e.target.value)}
                      className="holding-input"
                      min="0"
                      step="0.01"
                    />
                    <button type="button" className="remove-btn" onClick={() => removeHolding(index)} title="Remove" aria-label="Remove holding">
                      ×
                    </button>
                  </div>
                ))}
                <div className="holding-tools">
                  <button type="button" className="add-btn" onClick={addHolding}>+ Add a holding</button>
                  <label className="import-btn">
                    Import from a screenshot
                    <input type="file" accept="image/*" hidden onChange={handleImportImage} />
                  </label>
                  {importStatus && <span className="import-status">{importStatus}</span>}
                </div>
                {(formData.holdings || []).length === 0 && (
                  <p className="step-hint">Add at least 2 holdings to unlock portfolio analysis.</p>
                )}
              </div>
            </div>
          )}

          {/* ─── STEP 3: Preferences ─── */}
          {currentStep === 3 && (
            <div className="step-content">
              <div className="form-section">
                <h3>Investment Goals</h3>
                <div className="checkbox-grid">
                  {INVESTMENT_GOALS.map(goal => (
                    <label key={goal} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.investment_goals.includes(goal)}
                        onChange={() => handleMultiSelect('investment_goals', goal)}
                      />
                      {goal}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <h3>Risk Tolerance</h3>
                <select
                  value={formData.risk_tolerance}
                  onChange={(e) => handleInputChange('risk_tolerance', e.target.value)}
                >
                  <option value="">Select risk tolerance</option>
                  <option value="Conservative">Conservative - Preserve capital</option>
                  <option value="Moderate">Moderate - Balanced growth</option>
                  <option value="Aggressive">Aggressive - High growth potential</option>
                </select>
              </div>

              <div className="form-section">
                <h3>Investment Horizon</h3>
                <select
                  value={formData.investment_horizon}
                  onChange={(e) => handleInputChange('investment_horizon', e.target.value)}
                >
                  <option value="">Select time horizon</option>
                  <option value="Short-term (1-3 years)">Short-term (1-3 years)</option>
                  <option value="Medium-term (3-7 years)">Medium-term (3-7 years)</option>
                  <option value="Long-term (7+ years)">Long-term (7+ years)</option>
                </select>
              </div>

              <div className="form-section">
                <h3>Monthly Investment Capacity</h3>
                <select
                  value={formData.monthly_investment}
                  onChange={(e) => handleInputChange('monthly_investment', e.target.value)}
                >
                  <option value="">Select monthly investment</option>
                  {currencyData.investmentRanges.monthly.map((range, idx) => (
                    <option key={idx} value={range.value}>{range.display}</option>
                  ))}
                </select>
              </div>

              <div className="form-section">
                <h3>Current Portfolio Value</h3>
                <select
                  value={formData.current_portfolio}
                  onChange={(e) => handleInputChange('current_portfolio', e.target.value)}
                >
                  <option value="">Select portfolio size</option>
                  {currencyData.investmentRanges.portfolio.map((range, idx) => (
                    <option key={idx} value={range.value}>{range.display}</option>
                  ))}
                </select>
              </div>

              <div className="form-section">
                <h3>Preferred Sectors</h3>
                <div className="checkbox-grid">
                  {SECTORS.map(sector => (
                    <label key={sector} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.preferred_sectors.includes(sector)}
                        onChange={() => handleMultiSelect('preferred_sectors', sector)}
                      />
                      {sector}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <h3>Investment Style</h3>
                <select
                  value={formData.investment_style}
                  onChange={(e) => handleInputChange('investment_style', e.target.value)}
                >
                  <option value="">Select investment style</option>
                  <option value="Value Investing">Value Investing</option>
                  <option value="Growth Investing">Growth Investing</option>
                  <option value="Index Investing">Index Investing</option>
                  <option value="Dividend Investing">Dividend Investing</option>
                  <option value="Active Trading">Active Trading</option>
                </select>
              </div>
            </div>
          )}

          {/* ─── Navigation ─── */}
          <div className="form-actions">
            {currentStep > 1 ? (
              <button type="button" className="cancel-btn" onClick={() => setCurrentStep(currentStep - 1)}>
                Back
              </button>
            ) : !inline ? (
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
            ) : (
              <span />
            )}
            <div className="action-right">
              {currentStep < 3 && (
                <button type="button" className="next-btn" onClick={() => setCurrentStep(currentStep + 1)}>
                  Next
                </button>
              )}
              <button
                type="button"
                className={`save-btn ${saving ? 'saving' : ''}`}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
